<template>
  <transition :name="$style.fade">
    <NotificationInPage
      v-if="showPopUp || isSwitchBack"
      :actionText="actionText"
      :class="$style.onTop"
      :data-test-id="$testId('notification')"
      :notification="notificationOptions"
      @action="handleActionButton"
      @close="handleClose"
    >
      <template
        v-slot:message
      >
        <ParagraphBody
          :data-test-id="$testId('message')"
          :hasMargin="false"
          weight="bold"
        >
          {{ bodyText }}
        </ParagraphBody>
      </template>
    </NotificationInPage>
  </transition>
</template>

<script>
import {
  NotificationInPage,
  ParagraphBody,
  Types,
} from '@jumpcloud/ui-components';
import { cloneDeep } from 'lodash';
import { mapActions, mapGetters } from 'vuex';

const { NotificationConfig } = Types;

const betterValuePackageModule = 'BetterValuePackageModule';
const checkoutModel = 'CheckoutModel';

export default {
  name: 'RecommendedPackagePopUp',

  components: {
    NotificationInPage,
    ParagraphBody,
  },

  props: {
    ctaText: {
      type: String,
      required: true,
    },
    showCloseButton: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      isSwitchBack: false,
      showPopUp: false,
      notificationOptions: NotificationConfig({
        icon: 'info',
        isMarkdown: false,
        isSingleLineAction: false,
        showCloseButton: this.showCloseButton,
        type: 'informational',
      }),
    };
  },

  computed: {
    ...mapGetters(betterValuePackageModule, [
      'betterValuePackage',
    ]),

    ...mapGetters(checkoutModel, {
      subscription: 'item',
      previousSubscription: 'previousItem',
    }),

    actionText() {
      return this.isSwitchBack ? 'Switch Back' : this.ctaText;
    },

    bodyText() {
      return this.isSwitchBack
        ? `You have successfully saved money by switching to ${this.subscription.displayName}.`
        : `Get these features for $${this.displaySavings}/user less with ${this.betterValuePackage?.displayName}${this.plusAddOns}`;
    },

    displaySavings() {
      const savings = this.betterValuePackage?.savings;
      return savings % 1 !== 0
        ? savings.toLocaleString(undefined, { minimumFractionDigits: 2 })
        : savings;
    },

    plusAddOns() {
      return this.betterValuePackage?.addOns.length ? ' + add-ons' : '';
    },
  },

  watch: {
    betterValuePackage: {
      handler(newValue) {
        this.showPopUp = !!newValue;
      },
      immediate: true,
    },
  },

  methods: {
    ...mapActions(checkoutModel, {
      updatePreviousSubscription: 'updatePreviousItem',
      updateSubscription: 'updateItem',
    }),

    handleActionButton() {
      this.$emit('clickCta', cloneDeep(this.betterValuePackage), this.isSwitchBack);

      if (this.isSwitchBack) {
        // if admin switches from Jumpcloud Platform back to SSO or Core plans via A La Carte
        // don't show cloud directory as an add-on
        delete this.previousSubscription.components;

        this.updateSubscription(this.previousSubscription);
      } else {
        this.updatePreviousSubscription(this.subscription);
        this.updateSubscription(this.betterValuePackage);
      }

      if (this.$hasFeatureFlag(this.$featureFlags.purchaseAddOnsInProduct)) {
        this.isSwitchBack = !this.isSwitchBack;
      }
    },

    handleClose() {
      this.showPopUp = false;
    },
  },
};
</script>

<style scoped module>
@value announcementZIndex from '~css/z-index.css';
@value slowEaseFade from '~css/transitions.css';

.fade,
.fade-enter-active,
.fade-leave-active {
  transition: slowEaseFade;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.onTop {
  z-index: announcementZIndex;
}

</style>
